import React, { useState } from 'react';
import { Section, Stack, Center } from '@fiverr-private/layout_components';
import { Text, Heading } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import { AppI18n, appTranslate } from '../../../i18n';
import { useLandingPage } from '../../context/LandingPageContext';
import { useFetchGenerations } from '../../hooks/useFetchGenerations';
import { useVerticalFlowEvents } from '../../hooks/useVerticalFlowEvents';
import { sectionWithGradientBackgroundClass } from '../../styles/shared.ve.css';
import { STEPS_SECTION_ID } from '../../consts';
import { introTitleClass } from './styles.ve.css';
import { PromptBoxTagKey } from './types';
import { PromptTextArea } from './PromptTextArea';

const MIN_INPUT_LENGTH = 20;
const MAX_INPUT_LENGTH = 6000;

export const PromptBox = () => {
    const [inputValue, setInputValue] = useState('');
    const [isTextAreaActive, setIsTextAreaActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { setIsBottomDrawerOpen } = useLandingPage();
    const { fetchGenerations } = useFetchGenerations();
    const { reportButtonClickEvent } = useVerticalFlowEvents('Generate brief');
    const { reportButtonClickEvent: reportButtonClickCannedMessages } = useVerticalFlowEvents('Canned messages');

    const isPromptAreaOpen = inputValue.length > 0 || isTextAreaActive;
    const isValidInput = inputValue.length >= MIN_INPUT_LENGTH && inputValue.length <= MAX_INPUT_LENGTH;

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const handleGenerateBrief = async () => {
        try {
            setIsBottomDrawerOpen(true);
            fetchGenerations(inputValue);
        } catch (err) {
            logger.error(err as Error);
        } finally {
            setIsLoading(false);
            reportButtonClickEvent();
        }
    };

    const handleTagClicked = (tagKey: PromptBoxTagKey) => {
        const translatedContent = appTranslate(`prompt_box.tags.${tagKey}.content`);
        setInputValue(translatedContent.toString());

        const translatedName = appTranslate(`prompt_box.tags.${tagKey}.name`);
        reportButtonClickCannedMessages(translatedName.toString());
    };

    const handleScrollToStepsSection = () => {
        const element = document.getElementById(STEPS_SECTION_ID);

        if (element) {
            const offsetTop = element.getBoundingClientRect().top + window.scrollY;

            window.scrollTo({
                top: offsetTop - 40,
                behavior: 'smooth',
            });
        }
    };

    return (
        <Section fullWidth className={sectionWithGradientBackgroundClass}>
            <Center
                textAlign="center"
                paddingX="8"
                paddingTop={{ default: '14', lg: '24' }}
                paddingBottom={{ default: '20', lg: '28' }}
            >
                <Heading className={introTitleClass} maxWidth="628px">
                    <AppI18n k="prompt_box.title" />
                </Heading>
                <Text size="b_xl" marginTop="6" maxWidth="722px" hidden={{ default: true, lg: false }}>
                    <AppI18n k="prompt_box.description" />
                </Text>
                <Stack
                    direction="column"
                    gap="4"
                    maxWidth={{ lg: '680px', default: '378px', md: '450px' }}
                    marginTop={{ default: '10', lg: '14' }}
                    width="100%"
                >
                    <PromptTextArea
                        value={inputValue}
                        isActive={isPromptAreaOpen}
                        isLoading={isLoading}
                        isError={isValidInput}
                        onChange={handleInputChange}
                        onFocus={() => setIsTextAreaActive(true)}
                        onBlur={() => setIsTextAreaActive(false)}
                        onGenerateBrief={handleGenerateBrief}
                        onTagClick={handleTagClicked}
                    />
                    <Text
                        fontStyle="italic"
                        alignSelf="flexEnd"
                        justifySelf="flexEnd"
                        cursor="pointer"
                        color="bodySecondary"
                        size="b_xs"
                        onClick={handleScrollToStepsSection}
                    >
                        <AppI18n k="how_it_works.button_text" />
                    </Text>
                </Stack>
            </Center>
        </Section>
    );
};
